/**
 * Login page - integrates with POST /api/auth/login
 */

import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ThemeSettings from '../components/ThemeSettings';

const FEATURES = [
  'Collect semester and admission feedback securely',
  'Role-based dashboards for students, faculty, and admins',
  'Analytics and reports for institutional insights',
];

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [instituteSlug, setInstituteSlug] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await login(email, password, instituteSlug.trim() || undefined);
      navigate(from, { replace: true });
    } catch (err) {
      const res = err.response;
      const code = res?.data?.error?.code;
      let msg = res?.data?.error?.message || err.message || 'Login failed';
      if (code === '28P01') {
        msg = 'Server database configuration error. Please try again later or contact support.';
      } else if (res?.status === 401 && msg.toLowerCase().includes('credential')) {
        msg += ' Ensure mock users exist (see database/CREDENTIALS.md).';
      } else if (code === 'SPECIFY_INSTITUTE') {
        msg = 'Multiple accounts found. Enter your institute slug below.';
      }
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      <aside className="auth-brand" aria-hidden="true">
        <div className="auth-brand-content">
          <div className="auth-brand-logo">360</div>
          <h1 className="auth-brand-title">Faculty Feedback System</h1>
          <p className="auth-brand-text">
            A professional platform for 360-degree academic feedback, designed for institutes that value quality and transparency.
          </p>
        </div>
        <ul className="auth-brand-features">
          {FEATURES.map((feature) => (
            <li key={feature} className="auth-brand-feature">
              <span className="auth-brand-feature-dot" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </aside>

      <div className="auth-panel relative">
        <div className="auth-card">
        <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10">
            <div
              className="p-2 rounded-lg"
              style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-color)' }}
            >
              <ThemeSettings />
            </div>
          </div>

          <div className="auth-mobile-brand">
            <div className="auth-mobile-logo">360</div>
            <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
              Faculty Feedback System
            </p>
          </div>

          <div className="auth-card-inner">
            <div className="text-center mb-8">
              <h2 className="page-title text-xl sm:text-2xl">Sign in</h2>
              <p className="page-subtitle">Enter your credentials to access the portal</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="alert-error" role="alert">
                  {error}
                </div>
              )}
              <div>
                <label htmlFor="email" className="input-label">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="input"
                  placeholder="you@university.edu"
                />
              </div>
              <div>
                <label htmlFor="password" className="input-label">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="input"
                  placeholder="••••••••"
                />
              </div>
              <div>
                <label htmlFor="instituteSlug" className="input-label">
                  Institute slug <span style={{ color: 'var(--text-muted)' }} className="font-normal">(optional)</span>
                </label>
                <input
                  id="instituteSlug"
                  type="text"
                  value={instituteSlug}
                  onChange={(e) => setInstituteSlug(e.target.value)}
                  autoComplete="off"
                  className="input"
                  placeholder="e.g. pune-institute-technology"
                />
                <p className="input-hint">
                  Leave blank for Super Admin. Required if you have accounts in multiple institutes.
                </p>
              </div>
              <button type="submit" disabled={submitting} className="btn-primary w-full py-3">
                {submitting ? 'Signing in…' : 'Sign in'}
              </button>
            </form>

            <p className="mt-6 text-center text-sm" style={{ color: 'var(--text-secondary)' }}>
              New institute?{' '}
              <Link to="/register" className="auth-footer-link">
                Register your institute
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
